import type { AdminEventKey } from '@floorpoll/shared';
import { apiBeacon, hasAuthToken } from './api.js';

/**
 * Console telemetry: counts what signed-in people do, so the operator
 * dashboard can answer "is anyone using this, and what are they using".
 *
 * Counts, not events. The buffer holds `{ pollOpened: 3 }`, never three
 * timestamped records — there is no ordering, no session replay, and no way to
 * reconstruct one person's path through the app. That is deliberate: the
 * dashboard's contract is aggregate-only, and a client that never collects the
 * detail cannot leak it.
 *
 * Batched rather than one request per click for the reason `recordActivity`
 * piggybacks on `GET /me`: a poll being presented fires `screenAdvanced` every
 * few seconds for an hour, and a request each would be the busiest endpoint in
 * the system by an order of magnitude.
 */

const FLUSH_INTERVAL_MS = 20_000;

let buffer: Partial<Record<AdminEventKey, number>> = {};
let timer: ReturnType<typeof setTimeout> | null = null;

function pending(): boolean {
  for (const key in buffer) if (buffer[key as AdminEventKey]) return true;
  return false;
}

/**
 * Sends what is buffered and clears it.
 *
 * The buffer is swapped out *before* the request rather than cleared after, so
 * actions taken while the flush is in flight accumulate into the next batch
 * instead of being wiped by a clear that arrives after them.
 *
 * A failed flush drops its counts rather than restoring them. Re-queueing
 * would turn an outage into an ever-growing buffer that lands as one enormous
 * spike when the network returns, which is worse than a hole — a hole reads as
 * a hole, a spike reads as a record day.
 */
export function flushEvents(): void {
  // Cleared whoever called: an interval that already fired has nothing left to
  // do, and a `pagehide` flush that left the timer armed would leave `track`
  // believing a flush was still scheduled and never arm another one — every
  // count after the first backgrounding would then wait for the *next*
  // backgrounding instead of the next interval.
  if (timer) clearTimeout(timer);
  timer = null;

  if (!pending() || !hasAuthToken()) return;
  const counts = buffer;
  buffer = {};
  void apiBeacon('/me/events', { counts });
}

/**
 * Records one console action.
 *
 * Safe to call from anywhere, including a render path: it does arithmetic on
 * an object and sets a timer. Nothing here awaits, throws, or touches the
 * network — the flush does, on its own schedule.
 */
export function track(key: AdminEventKey, count = 1): void {
  buffer[key] = (buffer[key] ?? 0) + count;
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flushEvents();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Wires the flush triggers. Called once, from the app root.
 *
 * `visibilitychange` and `pagehide` rather than `beforeunload`: a phone that
 * backgrounds the console fires neither `beforeunload` nor `unload`, and a
 * presenter switching apps mid-session is the normal case here, not the edge
 * one. Both are registered because a tab can be hidden without unloading and
 * unloaded without ever being hidden.
 */
export function startEventFlushing(): () => void {
  const onHidden = () => {
    if (document.visibilityState === 'hidden') flushEvents();
  };

  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', flushEvents);

  return () => {
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', flushEvents);
    if (timer) clearTimeout(timer);
    timer = null;
  };
}
